import { useMemo } from 'react'
import { LineChart, Line, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts'
import { formatTokens, formatCost } from '@lens/lib/decode'
import type { ReplayTurn, CompactionEvent } from '@lens/types/claude'
import { CHART_TOOLTIP_STYLE, ChartGrid, ChartXAxis, ChartYAxis } from '../../chart-primitives'
import { SectionHeading } from '../../../section-heading'

interface Props {
 turns: ReplayTurn[]
 compactions: CompactionEvent[]
}

export function TokenAccumulationChart({ turns, compactions }: Props) {
 const data = useMemo(() => {
  const points: Array<{ turn: number; tokens: number; cost: number; label: string }> = []
  let cumCost = 0
  let cumTokens = 0
  let turnIdx = 0

  for (const t of turns) {
   turnIdx++
   if (t.type === 'assistant' && t.usage) {
    const u = t.usage
    cumTokens = (u.input_tokens ?? 0) + (u.cache_read_input_tokens ?? 0)
    cumCost += t.estimated_cost ?? 0
    points.push({ turn: turnIdx, tokens: cumTokens, cost: cumCost, label: `Turn ${turnIdx}` })
   }
  }
  return points
 }, [turns])

 const compactionTurnIndices = useMemo(
  () => compactions.map((c) => c.turn_index),
  [compactions],
 )

 if (data.length === 0) return null

 return (
  <div className="surface-edge rounded bg-card p-4">
   <SectionHeading as="h3" className="mb-3" title="Token Accumulation per Turn" />
   <ResponsiveContainer width="100%" height={180}>
    <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
     <ChartGrid />
     <ChartXAxis
      dataKey="turn"
      label={{ value: 'Turn', position: 'insideBottom', offset: -2, fill: 'var(--muted-foreground)' }}
     />
     <ChartYAxis tickFormatter={formatTokens} width={48} />
     <Tooltip
      contentStyle={CHART_TOOLTIP_STYLE}
      formatter={(val, name) => {
       const num = typeof val === 'number' ? val : 0
       const isTokens = name === 'tokens'
       return [
        isTokens ? formatTokens(num) : formatCost(num),
        isTokens ? 'Context tokens' : 'Cumulative cost',
       ]
      }}
     />
     {compactionTurnIndices.map((idx) => (
      <ReferenceLine
       key={idx}
       x={idx}
       stroke="var(--chart-4)"
       strokeDasharray="4 2"
       label={{ value: '◆', position: 'top' }}
      />
     ))}
     <Line
      type="monotone"
      dataKey="tokens"
      stroke="var(--chart-1)"
      strokeWidth={1.5}
      dot={false}
      activeDot={{ r: 3 }}
      isAnimationActive={false}
     />
    </LineChart>
   </ResponsiveContainer>
  </div>
 )
}
